import React, { forwardRef, useImperativeHandle, useMemo } from 'react';
import { ResultOf, TypedDocumentNode, VariablesOf } from '@graphql-typed-document-node/core';
import Select, { SelectOptions, SelectProps } from '@/components/Select';

import { camelCase } from '@/utils';
import useDb from '@/hooks/useDb';

type SelectFromQueryOption = { label: string; value?: string; values?: string[] };
export type SelectFromQueryProps<DocumentNode extends TypedDocumentNode = TypedDocumentNode> = {
  query: DocumentNode;
  selector: (data: ResultOf<DocumentNode>) => SelectFromQueryOption[];
  /** @deprecated use 'options.cache' instead */
  cache?: string;
  /** @deprecated use 'options.variables' instead */
  params?: { variables?: VariablesOf<DocumentNode> }; //TODO: update useDb to use graphApi
  options?: {
    cache?: string;
    variables?: VariablesOf<DocumentNode>;
    ttl?: number;
  } & SelectOptions;
} & SelectProps;

export type SelectFromQueryRef = (HTMLButtonElement | HTMLInputElement) & { refetch: () => void };

const SelectFromQuery = <Props extends SelectFromQueryProps>(
  { query, selector = (a: any): SelectFromQueryOption[] => a, options = {}, cache, params, ...props }: Props,
  ref: React.ForwardedRef<SelectFromQueryRef>
): JSX.Element => {
  options.cache = options?.cache || cache;
  options.variables = options?.variables || params?.variables;
  options.ttl = options?.ttl || 1;

  const dropdownButtonRef = React.useRef<HTMLButtonElement>(null);
  useImperativeHandle(ref, () => ({
    ...(dropdownButtonRef.current || ({} as HTMLButtonElement)),
    refetch,
  }));

  const { data, loading, refetch } = useDb(
    options?.cache || props?.name || camelCase(query?.definitions?.[0]?.['name']?.['value']),
    query,
    {
      selector,
      ...params,
      variables: options?.variables,
      ttl: options?.ttl,
    }
  );

  const content = useMemo(
    (): JSX.Element[] =>
      (selector?.(data) || []).map(
        ({ label, ...optionProps }: SelectFromQueryOption, o: number): JSX.Element => (
          <option {...optionProps} key={o}>
            {label}
          </option>
        )
      ),
    [data, selector]
  );

  return (
    <Select {...props} loading={!(content || []).length && !!loading} options={options} ref={dropdownButtonRef}>
      {content}
    </Select>
  );
};
const SelectFromQueryWithRef = forwardRef(SelectFromQuery);

export default React.memo(SelectFromQueryWithRef);
